// 不通知區（docs/mute-zones.md §11，M2）網頁畫面：
//   MuteRowActions —— 事件列上的［這種不要再通知］（可設的物件事件）/「在不通知區：名稱」+［這則其實要通知］（muted）
//   MuteZoneEditor —— 在事件截圖上圈框（拖移、拉右下角）、名稱、種類、期限 → 建立
//   MutedHiddenLine —— 時間軸「另有 N 則在不通知區」
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as RPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { loadEventSnapshot } from './eventsLib';
import {
  DEFAULT_BOX, MUTE_LABELS, canMute, createMuteZone, isMuted, moveBox, muteLabelOf, reportMuteMiss, resizeBox, useMuteZoneName,
} from './muteZones';
import type { Box, MuteLabel } from './muteZones';

type Ev = { payload?: Record<string, unknown>; has_snapshot?: boolean; eid?: string; event_ts: number };

export function MuteRowActions({ deviceId, ev, onChanged }: { deviceId: string; ev: Ev; onChanged?: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const zoneId = typeof ev.payload?.mute_zone_id === 'string' ? (ev.payload.mute_zone_id as string) : undefined;
  const name = useMuteZoneName(deviceId, zoneId);
  if (isMuted(ev)) {
    return (
      <span className="mz-row" data-testid="mz-muted">
        <span className="evt-unconf mz-badge">{t('muteZones.inZone', { name: name ?? t('muteZones.section') })}</span>
        {zoneId && !note && (
          <button className="btn-ghost mz-btn" data-testid="mz-missed"
            onClick={(e) => {
              e.stopPropagation();
              void reportMuteMiss(deviceId, zoneId, ev.eid ?? String(ev.event_ts)).then(() => setNote(t('muteZones.missedDone')));
            }}>
            {t('muteZones.missed')}
          </button>
        )}
        {note && <span className="text-secondary mz-note" role="status">{note}</span>}
      </span>
    );
  }
  if (!canMute(ev)) return null;
  return (
    <span className="mz-row">
      {!note && (
        <button className="btn-ghost mz-btn" data-testid="mz-open" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
          {t('muteZones.button')}
        </button>
      )}
      {note && <span className="text-secondary mz-note" role="status" data-testid="mz-saved">{note}</span>}
      {open && (
        <MuteZoneEditor deviceId={deviceId} ev={ev} onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); setNote(t('muteZones.saved')); onChanged?.(); }} />
      )}
    </span>
  );
}

export function MuteZoneEditor({ deviceId, ev, onClose, onSaved }: { deviceId: string; ev: Ev; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  useFocusTrap(true, ref);
  const [src, setSrc] = useState<string | null>(null);
  const [box, setBox] = useState<Box>(DEFAULT_BOX);
  const [name, setName] = useState(t('muteZones.defaultName'));
  const first = muteLabelOf(ev);
  const [labels, setLabels] = useState<MuteLabel[]>(first ? [first] : ['person']);
  const [always, setAlways] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const drag = useRef<{ mode: 'move' | 'resize'; x: number; y: number; b: Box } | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    void loadEventSnapshot(deviceId, ev.eid ?? '', ac.signal).then((u) => setSrc(u));
    return () => ac.abort();
  }, [deviceId, ev.eid]);

  const down = (mode: 'move' | 'resize') => (e: RPointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { mode, x: e.clientX, y: e.clientY, b: box };
  };
  const move = (e: RPointerEvent) => {
    const d = drag.current;
    const r = imgRef.current?.getBoundingClientRect();
    if (!d || !r || r.width === 0) return;
    const dx = (e.clientX - d.x) / r.width;
    const dy = (e.clientY - d.y) / r.height;
    setBox(d.mode === 'move' ? moveBox(d.b, dx, dy) : resizeBox(d.b, dx, dy));
  };
  const up = () => { drag.current = null; };

  const save = async () => {
    if (!labels.length || !name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createMuteZone(deviceId, { name: name.trim(), labels, box, expires_days: always ? -1 : 30, from_event: ev.eid ?? String(ev.event_ts) });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError && e.code === 'MUTE_ZONE_LIMIT' ? t('muteZones.limit')
        : e instanceof ApiError && e.code === 'INVALID_MUTE_ZONE' ? t('muteZones.badBox') : t('common.networkError'));
    } finally {
      setBusy(false);
    }
  };

  const pct = (v: number) => `${v * 100}%`;
  return (
    <div role="dialog" aria-modal="true" aria-label={t('muteZones.title')} data-testid="mz-editor"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 'var(--spacing-8)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card mz-card" ref={ref} onClick={(e) => e.stopPropagation()}>
        <h2 className="mz-title">{t('muteZones.title')}</h2>
        <p className="text-secondary mz-hint">{t('muteZones.hint')}</p>
        <div className="mz-img" ref={imgRef} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
          {src ? <img src={src} alt="" draggable={false} /> : <div className="mz-img-empty">{t('common.loading')}</div>}
          <div className="mz-box" data-testid="mz-box" data-box={box.join(',')} onPointerDown={down('move')}
            style={{ left: pct(box[0]), top: pct(box[1]), width: pct(box[2] - box[0]), height: pct(box[3] - box[1]) }}>
            <span className="mz-handle" data-testid="mz-handle" onPointerDown={down('resize')} />
          </div>
        </div>
        <label className="mz-field">
          <span>{t('muteZones.name')}</span>
          <input value={name} maxLength={20} onChange={(e) => setName(e.target.value)} data-testid="mz-name" />
        </label>
        <div className="mz-field">
          {MUTE_LABELS.map((l) => (
            <label key={l} className="mz-check">
              <input type="checkbox" checked={labels.includes(l)} data-testid={`mz-label-${l}`}
                onChange={(e) => setLabels((xs) => (e.target.checked ? [...xs, l] : xs.filter((x) => x !== l)))} />
              {t(`muteZones.label.${l}`)}
            </label>
          ))}
        </div>
        <div className="mz-field">
          <label className="mz-check"><input type="radio" name="mz-dur" checked={!always} onChange={() => setAlways(false)} data-testid="mz-30d" />{t('muteZones.days30')}</label>
          <label className="mz-check"><input type="radio" name="mz-dur" checked={always} onChange={() => setAlways(true)} data-testid="mz-always" />{t('muteZones.always')}</label>
        </div>
        {err && <p className="text-danger" role="alert">{err}</p>}
        <div className="mz-actions">
          <button className="btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn-primary" disabled={busy || !labels.length || !name.trim()} onClick={() => void save()} data-testid="mz-save">
            {t('muteZones.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MutedHiddenLine({ count, showing, onToggle }: { count: number; showing: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  if (count <= 0 && !showing) return null;
  return (
    <button className="btn-ghost mz-hidden-line" onClick={onToggle} data-testid="mz-hidden-line" aria-pressed={showing}>
      {showing ? t('muteZones.backToAll') : t('muteZones.hiddenCount', { count })}
    </button>
  );
}
